<template>
	<div class="dynamic-charts-wrapper">
		<!-- Chart Header -->
		<div class="text-center">
			<h4 class="font-semibold text-gray-800">
				{{ props.title || props.emissionFactors.emissionFactorName || "Chart" }}
			</h4>
		</div>

		<!-- No Data Message -->
		<div v-if="hasNoData" class="flex items-center justify-center h-full min-h-[300px]">
			<div class="text-center p-8 bg-gray-100 rounded-lg">
				<i class="pi pi-chart-line text-6xl text-gray-300 mb-4"></i>
				<p class="text-xl font-semibold text-gray-500">No Data Available</p>
				<p class="text-sm text-gray-400 mt-2">All values are zero or empty</p>
			</div>
		</div>

		<!-- Charts -->
		<div v-else class="flex mt-2 mb-2 h-full relative">
			<Chart
				v-if="!showPieChart"
				class="w-full h-full"
				type="bar"
				:data="barChartData"
				:options="horizontalStackOptions"
				chart-id="dynamic-bar-chart"
				dataset-id-key="label"
			/>
			<Chart
				v-if="showPieChart"
				class="w-full h-full"
				type="pie"
				:data="pieChartData"
				:options="pieChartOptions"
				chart-id="dynamic-pie-chart"
				dataset-id-key="label"
			/>
			<!-- Legend hover tooltip -->
			<div v-if="legendTooltip.visible" class="legend-tooltip" :style="{ left: legendTooltip.x + 'px', top: legendTooltip.y + 'px' }">
				<div v-for="(line, idx) in legendTooltip.lines" :key="idx">{{ line }}</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { EmissionFactors } from "../../entities/NSwagGenerated"
import { generateCategoricalChartColors } from "../composables/chartColorHelper"
import Chart from "primevue/chart"

// Interface for chart data items
export interface ChartDataItem {
	name: string
	emissionFactors: { [key: string]: number }
}

interface Props {
	showPieChart: boolean
	emissionFactors: EmissionFactors
	chartData: ChartDataItem[]
	title?: string
}

const props = defineProps<Props>()

const graphLabels = computed(() => props.chartData.map((item) => item.name))

// Legend hover tooltip state
const legendTooltip = ref({ visible: false, x: 0, y: 0, lines: [] as string[] })

const barGraphDatasetData = computed(() =>
	props.chartData.map((item) =>
		props.emissionFactors.emissionFactorName ? (item.emissionFactors[props.emissionFactors.emissionFactorName] ?? 0) : 0
	)
)

// Check if all data values are null, undefined, or 0
const hasNoData = computed(() => {
	const data = barGraphDatasetData.value
	if (!data || data.length === 0) return true

	return data.every((value) => value === null || value === undefined || value === 0)
})

const chartSliceColors = computed(() => generateCategoricalChartColors(graphLabels.value))

// Chart data computed properties
const commonGraphData = computed(() => ({
	labels: graphLabels.value,
	datasets: [
		{
			label: "", // Remove label since we have header
			backgroundColor: chartSliceColors.value,
			data: barGraphDatasetData.value,
		},
	],
}))

const barChartData = computed(() => ({
	...commonGraphData.value,
	datasets: commonGraphData.value.datasets.map((dataset) => ({
		...dataset,
		borderColor: "#1988E5",
	})),
}))

const pieChartData = computed(() => ({
	...commonGraphData.value,
	datasets: commonGraphData.value.datasets.map((dataset) => ({
		...dataset,
		borderColor: "#fff",
	})),
}))

// Chart options
const horizontalStackOptions = {
	indexAxis: "y",
	maintainAspectRatio: false,
	responsive: true,
	layout: {
		padding: {
			left: 10,
			right: 20,
			top: 10,
			bottom: 10,
		},
	},
	scales: {
		x: {
			beginAtZero: true,
		},
		y: {
			beginAtZero: true,
			ticks: {
				maxRotation: 0,
				minRotation: 0,
				font: {
					size: 11,
				},
				// Use word wrapping instead of truncation
				callback: function (value: any, index: number): string | string[] {
					const labels = graphLabels.value
					const label = labels[index] || ""

					// Split at "/" first for path-like labels, then word-wrap each part
					if (label.includes("/")) {
						return label.split("/").map((p: string) => p.trim())
					}

					// Split long labels into multiple lines
					if (label.length > 15) {
						const words = label.split(" ")
						const lines: string[] = []
						let currentLine = ""

						for (const word of words) {
							if ((currentLine + word).length > 15 && currentLine.length > 0) {
								lines.push(currentLine.trim())
								currentLine = word + " "
							} else {
								currentLine += word + " "
							}
						}
						if (currentLine.trim().length > 0) {
							lines.push(currentLine.trim())
						}

						return lines.length > 1 ? lines : label
					}
					return label
				},
			},
			// Increase spacing between bars
			grid: {
				display: true,
				color: "rgba(0, 0, 0, 0.1)",
			},
		},
	},
	plugins: {
		legend: {
			display: false,
		},
	},
}

const pieChartOptions = computed(() => ({
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: true,
			position: "bottom",
			onHover: function (event: any, legendItem: any, legend: any) {
				const fullLabel = legend.chart.data.labels[legendItem.index] || ""
				if (fullLabel.includes("/")) {
					const canvas = legend.chart.canvas as HTMLCanvasElement
					const rect = canvas.getBoundingClientRect()
					legendTooltip.value = {
						visible: true,
						x: event.native.clientX - rect.left + 10,
						y: event.native.clientY - rect.top - 40,
						lines: fullLabel.split("/").map((p: string) => p.trim()),
					}
				}
			},
			onLeave: function () {
				legendTooltip.value.visible = false
			},
			labels: {
				padding: 20,
				usePointStyle: true,
				generateLabels: function (chart: any) {
					const datasets = chart.data.datasets
					const labels = chart.data.labels || []
					if (!datasets.length) return []
					return labels.map((label: string, i: number) => {
						const meta = chart.getDatasetMeta(0)
						const style = meta.controller.getStyle(i)
						// Show only the last part after "/" for short legend text
						const shortLabel = label.includes("/") ? label.split("/").pop()!.trim() : label
						return {
							text: shortLabel,
							fillStyle: style.backgroundColor,
							strokeStyle: style.borderColor,
							lineWidth: style.borderWidth,
							pointStyle: "circle",
							hidden: !chart.getDataVisibility(i),
							index: i,
						}
					})
				},
			},
		},
		tooltip: {
			callbacks: {
				title: function (tooltipItems: any[]) {
					if (!tooltipItems.length) return ""
					const label = tooltipItems[0].label || ""
					// Split at "/" for multiline tooltip title
					if (label.includes("/")) {
						return label.split("/").map((p: string) => p.trim())
					}
					return label
				},
				label: function (context: any) {
					const value = context.raw || 0
					const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0)
					const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0"
					const formattedValue = typeof value === "number" ? value.toFixed(3) : value
					return `${formattedValue} (${percentage}%)`
				},
			},
		},
	},
}))
</script>

<style scoped>
.dynamic-charts-wrapper {
	width: 100%;
}
.legend-tooltip {
	position: absolute;
	background: rgba(0, 0, 0, 0.8);
	color: #fff;
	padding: 6px 10px;
	border-radius: 4px;
	font-size: 12px;
	line-height: 1.4;
	pointer-events: none;
	z-index: 10;
	white-space: nowrap;
}
</style>
